import React from 'react';
import { Check } from 'lucide-react';

export interface Step {
  label: string;
  description?: string;
}

type StepStatus = 'complete' | 'current' | 'upcoming';

interface Props {
  steps: Step[];
  currentStep: number;
  className?: string;
}

const Stepper: React.FC<Props> = ({ steps, currentStep, className = '' }) => {
  return (
    <ol className={`flex items-start gap-2 ${className}`}>
      {steps.map((step, idx) => {
        const status: StepStatus =
          idx < currentStep ? 'complete'
          : idx === currentStep ? 'current'
          : 'upcoming';

        const isLast = idx === steps.length - 1;

        return (
          <li key={step.label} className="flex flex-1 items-start gap-2">
            <div className="flex flex-1 flex-col items-center text-center">
              <div className="flex w-full items-center">
                <span className="flex-1 border-t-2 border-transparent" aria-hidden />
                <span
                  aria-hidden
                  className={[
                    'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold',
                    status === 'complete' &&
                      'border-brand-600 bg-brand-600 text-white',
                    status === 'current' &&
                      'border-brand-600 bg-white text-brand-700 ring-4 ring-brand-100',
                    status === 'upcoming' &&
                      'border-slate-300 bg-white text-slate-400',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {status === 'complete' ? <Check className="h-4 w-4" /> : idx + 1}
                </span>
                {!isLast ? (
                  <span
                    aria-hidden
                    className={`flex-1 border-t-2 ${
                      status === 'complete' ? 'border-brand-600' : 'border-slate-200'
                    }`}
                  />
                ) : (
                  <span className="flex-1 border-t-2 border-transparent" aria-hidden />
                )}
              </div>
              <p
                className={`mt-2 text-xs font-semibold uppercase tracking-wider ${
                  status === 'upcoming' ? 'text-slate-500' : 'text-slate-900'
                }`}
              >
                {step.label}
              </p>
              {step.description && (
                <p className="mt-0.5 text-xs text-slate-500">{step.description}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default Stepper;
